import { usePackageNamesOf } from '@/features/packages/packageNames';
import { useT } from '@/i18n/LocaleProvider';
import { builtInViewWords, DEFAULT_VIEW_ID_PREFIX } from './viewNames';

/**
 * I-1977: a view's name as the reader reads it — a built-in's in the reader's language
 * (`builtInViewWords`, by its catalog id), a package's view in the reader's language
 * (`usePackageNamesOf`), any other view as it was named. What is stored, and what a rename edits,
 * stays the name itself.
 */
export function usePackageViewName(view: {
  id?: string | null;
  workAreaId: string | null;
  name: string;
}): string {
  return usePackageViewNameState(view).name;
}

/**
 * {@link usePackageViewName} with whether the package's words are still on their way (I-2088): a
 * screen that draws the name waits with a skeleton, never the stored English first.
 */
export function usePackageViewNameState(view: {
  id?: string | null;
  workAreaId: string | null;
  name: string;
}): { name: string; loading: boolean } {
  const t = useT();
  const packageName = usePackageNamesOf(view.workAreaId);
  return {
    name: packageName(view.id ? builtInViewWords(t, view.id, view.name) : view.name),
    loading: Boolean(packageName.loading),
  };
}

/**
 * The same, for a view an address names (`viewNameOfUrlId`): its url id is a built-in's bare seed id
 * or a saved view's own id. Nothing named, nothing to read.
 */
export function useReaderViewName(
  workAreaId: string | null | undefined,
  urlId: string | null | undefined,
  name: string | null | undefined,
): string | undefined {
  const t = useT();
  const packageName = usePackageNamesOf(workAreaId);
  if (!name) return undefined;
  // I-2088: no title until the package's words in the reader's language are in.
  if (packageName.loading) return undefined;
  return packageName(urlId ? builtInViewWords(t, `${DEFAULT_VIEW_ID_PREFIX}${urlId}`, name) : name);
}
